'use client';

import Image from 'next/image';
import { motion } from 'framer-motion';
import Link from 'next/link';
import {
  Heart,
  Siren,
  Timer,
  Activity,
  MapPin,
  Radio,
  BarChart3,
  Shield,
  Camera,
  Map,
  FileText,
  Users,
  Building2,
  Flame,
  Zap,
  CheckCircle2,
  ArrowRight,
  Clock,
  TrendingUp,
  AlertTriangle,
  Navigation,
  Gauge,
  Stethoscope,
} from 'lucide-react';
import { PAGE_HERO_IMAGES } from '@/lib/marketing-images';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

export default function AmbulanceEmergencyPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ── Hero Section ───────────────────────────────────────────── */}
      <section className="relative overflow-hidden gradient-hero grid-bg pt-32 pb-20 px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-2 mb-6"
            >
              <Siren className="h-4 w-4 text-neon-magenta animate-pulse" />
              <span className="text-sm font-medium text-neon-magenta">Emergency Services</span>
            </motion.div>

            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="text-white">Every Second </span>
              <span className="text-gradient-warm">Counts</span>
            </h1>

            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-8 leading-relaxed">
              Optimize emergency response with real-time dispatch, GPS tracking, and response-time SLA monitoring.
              <span className="text-neon-magenta font-semibold"> Save lives faster.</span>
            </p>

            <div className="mx-auto mb-8 max-w-4xl">
              <div className="glass-card-heavy rounded-3xl p-2 border border-white/10">
                <Image
                  src={PAGE_HERO_IMAGES.ambulanceEmergency.src}
                  alt={PAGE_HERO_IMAGES.ambulanceEmergency.alt}
                  width={1600}
                  height={900}
                  className="h-auto w-full rounded-2xl object-cover"
                  priority
                />
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Link
                href="/contact"
                className="btn-neon px-8 py-4 rounded-xl text-lg font-semibold flex items-center gap-2 glow-magenta"
                style={{
                  background: 'linear-gradient(135deg, #f43f5e, #a855f7)',
                }}
              >
                Book a Demo
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link
                href="#features"
                className="btn-neon-outline px-8 py-4 rounded-xl text-lg font-semibold flex items-center gap-2"
                style={{
                  borderColor: 'rgba(244, 63, 94, 0.3)',
                  color: '#f43f5e',
                }}
              >
                Explore Features
              </Link>
            </motion.div>
          </motion.div>

          {/* Hero Stats */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16"
          >
            {[
              { label: 'Faster Response', value: '40%', icon: Timer },
              { label: 'Fleet Visibility', value: '99.9%', icon: Activity },
              { label: 'Cost Reduction', value: '35%', icon: TrendingUp },
              { label: 'Uptime', value: '99.9%', icon: Shield },
            ].map((stat, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="glass-card rounded-xl p-6 text-center"
              >
                <stat.icon className="h-8 w-8 text-neon-magenta mx-auto mb-3" />
                <div className="text-3xl font-bold text-gradient-warm mb-1">{stat.value}</div>
                <div className="text-sm text-slate-300">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Industry Challenges ───────────────────────────────────── */}
      <section className="py-20 px-6 lg:px-8 gradient-section-magenta">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-white">Critical Challenges in </span>
              <span className="text-gradient-warm">Emergency Response</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Every delay can mean the difference between life and death
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {[
              {
                title: 'Slow Dispatch',
                description: 'Manual dispatch processes delay critical response times, costing precious minutes when seconds matter.',
                icon: Clock,
                color: 'text-neon-magenta',
              },
              {
                title: 'No Real-Time Visibility',
                description: 'Dispatchers operate blind, unable to see vehicle locations, availability, or optimal routing in real-time.',
                icon: MapPin,
                color: 'text-neon-cyan',
              },
              {
                title: 'Poor Response Tracking',
                description: 'No automated SLA monitoring means response times go unmeasured, leading to compliance failures.',
                icon: AlertTriangle,
                color: 'text-neon-purple',
              },
              {
                title: 'Vehicle Maintenance Gaps',
                description: 'Unexpected breakdowns during emergencies compromise service reliability and patient safety.',
                icon: Gauge,
                color: 'text-neon-magenta',
              },
              {
                title: 'Driver Fatigue',
                description: 'Long shifts without monitoring lead to exhausted drivers, increasing accident risk and reducing response quality.',
                icon: Activity,
                color: 'text-neon-cyan',
              },
              {
                title: 'Communication Breakdowns',
                description: 'Fragmented systems prevent seamless coordination between dispatch, vehicles, and hospitals.',
                icon: Radio,
                color: 'text-neon-purple',
              },
            ].map((challenge, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="glass-card rounded-xl p-6 hover:border-neon-magenta/30 transition-all"
              >
                <challenge.icon className={`h-8 w-8 ${challenge.color} mb-4`} />
                <h3 className="text-xl font-semibold text-white mb-2">{challenge.title}</h3>
                <p className="text-slate-300 leading-relaxed">{challenge.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Our Solution ───────────────────────────────────────────── */}
      <section id="solution" className="py-20 px-6 lg:px-8 gradient-section">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-white">GlonexTrack: </span>
              <span className="text-gradient">Life-Saving Intelligence</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Real-time telematics platform designed specifically for emergency services
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-card-heavy rounded-xl p-8"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="rounded-lg bg-gradient-to-br from-neon-magenta/20 to-neon-purple/20 p-3">
                  <Navigation className="h-6 w-6 text-neon-magenta" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Real-Time Ambulance Tracking</h3>
                  <p className="text-slate-300 leading-relaxed">
                    See every vehicle&apos;s exact location, speed, and status on a live map. Dispatchers can instantly identify the nearest available unit and route them optimally.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-card-heavy rounded-xl p-8"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="rounded-lg bg-gradient-to-br from-neon-cyan/20 to-neon-teal/20 p-3">
                  <Zap className="h-6 w-6 text-neon-cyan" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Nearest-Unit Dispatch</h3>
                  <p className="text-slate-300 leading-relaxed">
                    AI-powered dispatch automatically selects the closest available ambulance based on real-time location, traffic conditions, and vehicle status.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="glass-card-heavy rounded-xl p-8"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="rounded-lg bg-gradient-to-br from-neon-purple/20 to-neon-magenta/20 p-3">
                  <BarChart3 className="h-6 w-6 text-neon-purple" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Automated SLA Reporting</h3>
                  <p className="text-slate-300 leading-relaxed">
                    Track response times automatically against regulatory SLAs. Generate compliance reports instantly and identify areas for improvement.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="glass-card-heavy rounded-xl p-8"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="rounded-lg bg-gradient-to-br from-neon-magenta/20 to-neon-cyan/20 p-3">
                  <Stethoscope className="h-6 w-6 text-neon-magenta" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Patient Transport Monitoring</h3>
                  <p className="text-slate-300 leading-relaxed">
                    Monitor patient transport routes, arrival times, and vehicle conditions. Ensure optimal care delivery throughout the journey.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-card-heavy rounded-xl p-8"
          >
            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-gradient-to-br from-neon-cyan/20 to-neon-purple/20 p-3">
                <Shield className="h-6 w-6 text-neon-cyan" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">Fleet Health Management</h3>
                <p className="text-slate-300 leading-relaxed mb-4">
                  Predictive maintenance alerts prevent breakdowns before they happen. Monitor engine health, battery levels, tire pressure, and more in real-time.
                </p>
                <div className="grid md:grid-cols-3 gap-4 mt-6">
                  {['Engine Diagnostics', 'Battery Monitoring', 'Tire Pressure Alerts'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="h-4 w-4 text-neon-cyan" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Key Features Grid ──────────────────────────────────────── */}
      <section id="features" className="py-20 px-6 lg:px-8 gradient-section-purple">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-white">Comprehensive </span>
              <span className="text-gradient-cool">Emergency Features</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Everything you need to optimize emergency response operations
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {[
              {
                title: 'GPS Tracking',
                description: 'Real-time location tracking with sub-second updates',
                icon: MapPin,
                color: 'text-neon-cyan',
                glow: 'glow-cyan',
              },
              {
                title: 'CAD Integration',
                description: 'Seamless integration with Computer-Aided Dispatch systems',
                icon: Radio,
                color: 'text-neon-purple',
                glow: 'glow-purple',
              },
              {
                title: 'Response Time Analytics',
                description: 'Detailed analytics on response times, routes, and performance',
                icon: Timer,
                color: 'text-neon-magenta',
                glow: 'glow-magenta',
              },
              {
                title: 'SLA Dashboards',
                description: 'Live dashboards monitoring compliance with response time SLAs',
                icon: BarChart3,
                color: 'text-neon-cyan',
                glow: 'glow-cyan',
              },
              {
                title: 'Driver Fatigue Detection',
                description: 'AI-powered detection of driver fatigue and unsafe behavior',
                icon: Activity,
                color: 'text-neon-magenta',
                glow: 'glow-magenta',
              },
              {
                title: 'Camera Monitoring',
                description: 'Live and recorded video feeds from vehicle cameras',
                icon: Camera,
                color: 'text-neon-purple',
                glow: 'glow-purple',
              },
              {
                title: 'Hospital Zone Geofencing',
                description: 'Automated alerts when vehicles enter or exit hospital zones',
                icon: Map,
                color: 'text-neon-cyan',
                glow: 'glow-cyan',
              },
              {
                title: 'Automated Reporting',
                description: 'Generate compliance and performance reports automatically',
                icon: FileText,
                color: 'text-neon-magenta',
                glow: 'glow-magenta',
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className={`glass-card rounded-xl p-6 hover:scale-105 transition-transform ${feature.glow}`}
              >
                <feature.icon className={`h-8 w-8 ${feature.color} mb-4`} />
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Use Cases ──────────────────────────────────────────────── */}
      <section className="py-20 px-6 lg:px-8 gradient-section">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-white">Trusted by </span>
              <span className="text-gradient">Emergency Organizations</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Serving diverse emergency service providers across the industry
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-8"
          >
            {[
              {
                title: 'Private Ambulance Services',
                description: 'Optimize fleet operations for private EMS providers. Reduce response times, improve patient outcomes, and maximize operational efficiency.',
                icon: Building2,
                color: 'text-neon-magenta',
                bgGradient: 'bg-gradient-to-br from-neon-magenta/20 to-neon-purple/20',
                features: ['Route Optimization', 'Patient Transport Tracking', 'Billing Integration'],
              },
              {
                title: 'Government EMS',
                description: 'Meet strict regulatory requirements with automated SLA monitoring and comprehensive reporting for municipal emergency services.',
                icon: Shield,
                color: 'text-neon-cyan',
                bgGradient: 'bg-gradient-to-br from-neon-cyan/20 to-neon-teal/20',
                features: ['Regulatory Compliance', 'Public Safety Reporting', 'Budget Optimization'],
              },
              {
                title: 'Hospital Patient Transport',
                description: 'Coordinate inter-facility transfers and patient transport with real-time tracking and arrival notifications.',
                icon: Heart,
                color: 'text-neon-purple',
                bgGradient: 'bg-gradient-to-br from-neon-purple/20 to-neon-magenta/20',
                features: ['Inter-Facility Transfers', 'Arrival Notifications', 'Patient Care Coordination'],
              },
              {
                title: 'Fire & Rescue Coordination',
                description: 'Coordinate multi-agency responses with shared visibility and real-time communication between fire, EMS, and rescue units.',
                icon: Flame,
                color: 'text-neon-magenta',
                bgGradient: 'bg-gradient-to-br from-neon-magenta/20 to-neon-cyan/20',
                features: ['Multi-Agency Coordination', 'Resource Sharing', 'Incident Management'],
              },
            ].map((useCase, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="glass-card-heavy rounded-xl p-8"
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className={`rounded-lg ${useCase.bgGradient} p-3`}>
                    <useCase.icon className={`h-8 w-8 ${useCase.color}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-3">{useCase.title}</h3>
                    <p className="text-slate-300 leading-relaxed mb-4">{useCase.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {useCase.features.map((feature, j) => (
                        <span
                          key={j}
                          className="text-xs px-3 py-1 rounded-full glass-card text-slate-300"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Impact Stats ───────────────────────────────────────────── */}
      <section className="py-20 px-6 lg:px-8 gradient-section-magenta">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-white">Proven </span>
              <span className="text-gradient-warm">Results</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Real impact from emergency services using GlonexTrack
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                value: '40%',
                label: 'Faster Response Times',
                description: 'Average reduction in time-to-scene',
                icon: Timer,
                color: 'text-neon-magenta',
              },
              {
                value: '99.9%',
                label: 'Fleet Visibility',
                description: 'Real-time tracking accuracy',
                icon: Activity,
                color: 'text-neon-cyan',
              },
              {
                value: '35%',
                label: 'Maintenance Cost Reduction',
                description: 'Through predictive maintenance',
                icon: TrendingUp,
                color: 'text-neon-purple',
              },
              {
                value: '50%',
                label: 'SLA Compliance Improvement',
                description: 'Better adherence to response time targets',
                icon: CheckCircle2,
                color: 'text-neon-magenta',
              },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="glass-card-heavy rounded-xl p-8 text-center"
              >
                <stat.icon className={`h-10 w-10 ${stat.color} mx-auto mb-4`} />
                <div className="text-5xl font-bold text-gradient-warm mb-2">{stat.value}</div>
                <div className="text-lg font-semibold text-white mb-2">{stat.label}</div>
                <div className="text-sm text-slate-300">{stat.description}</div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 glass-card-heavy rounded-xl p-8 text-center"
          >
            <p className="text-xl text-slate-300 mb-4">
              <span className="text-white font-semibold">Join hundreds of emergency service providers</span> who trust GlonexTrack to optimize their operations and save lives.
            </p>
            <div className="flex items-center justify-center gap-8 mt-6 text-sm text-slate-300">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-neon-cyan" />
                <span>500+ Organizations</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-neon-purple" />
                <span>50+ Countries</span>
              </div>
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-neon-magenta" />
                <span>10M+ Trips Tracked</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA Section ────────────────────────────────────────────── */}
      <section className="py-20 px-6 lg:px-8 gradient-hero">
        <div className="mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-card-heavy rounded-2xl p-12 text-center"
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-neon-magenta/20 to-neon-purple/20 mb-6"
            >
              <Siren className="h-10 w-10 text-neon-magenta" />
            </motion.div>

            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-white">Ready to Save </span>
              <span className="text-gradient-warm">More Lives?</span>
            </h2>

            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              See how GlonexTrack can transform your emergency response operations. Book a personalized demo and discover the life-saving potential of real-time telematics.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/contact"
                className="btn-neon px-8 py-4 rounded-xl text-lg font-semibold flex items-center gap-2 glow-magenta"
                style={{
                  background: 'linear-gradient(135deg, #f43f5e, #a855f7)',
                }}
              >
                Book a Demo
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link
                href="/pricing"
                className="btn-neon-outline px-8 py-4 rounded-xl text-lg font-semibold flex items-center gap-2"
                style={{
                  borderColor: 'rgba(244, 63, 94, 0.3)',
                  color: '#f43f5e',
                }}
              >
                View Pricing
              </Link>
            </div>

            <div className="mt-8 pt-8 border-t border-white/10">
              <p className="text-sm text-slate-300 mb-4">Trusted by leading emergency service providers</p>
              <div className="flex items-center justify-center gap-6 text-xs text-slate-300">
                <span>✓ HIPAA Compliant</span>
                <span>✓ SOC 2 Certified</span>
                <span>✓ 24/7 Support</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
